import { createContext, useContext, useEffect, useState } from "react";
import { AuthApi, getToken, setToken, clearToken } from "../api";

type AuthState =
  | { status: "loading" }
  | { status: "loggedOut" }
  | { status: "loggedIn"; userId: string; admin: boolean };

const authContext = createContext<{
  auth: AuthState;
  login: (input: { username: string; password: string }) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
}>({
  auth: { status: "loading" },
  login: async () => ({ success: false, error: "not ready" }),
  logout: () => {},
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [auth, setAuth] = useState<AuthState>({ status: "loading" });

  const load = async (token: string) => {
    const user = await AuthApi.getUser(token);
    if (!user?.success || !user?.data?.id) return undefined;
    return { userId: user.data.id as string, admin: !!user.data.admin };
  };

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setAuth({ status: "loggedOut" });
      return;
    }
    load(token).then((u) => {
      if (u) setAuth({ status: "loggedIn", ...u });
      else {
        clearToken();
        setAuth({ status: "loggedOut" });
      }
    });
  }, []);

  const login = async (input: { username: string; password: string }) => {
    const result = await AuthApi.login(input);
    if (!result?.success || !result?.token) return { success: false, error: result?.message || "Login failed" };
    setToken(result.token);
    const u = await load(result.token);
    if (!u) return { success: false, error: "Could not load user" };
    setAuth({ status: "loggedIn", ...u });
    return { success: true };
  };

  const logout = () => {
    clearToken();
    setAuth({ status: "loggedOut" });
  };

  return <authContext.Provider value={{ auth, login, logout }}>{children}</authContext.Provider>;
};

export const useAuth = () => useContext(authContext);
